import {
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Checkbox,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from "@mui/material";
import { ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { Project, Tag } from "../../models/Project";
import { FilterValues } from "../../models/global/FilterTypes";

interface ProjectFilterProps {
  projectList: Project[];
  onChange: (filters: FilterValues) => void;
  filters: FilterValues;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const ProjectFilters = ({
  onChange,
  filters,
  projectList,
}: ProjectFilterProps) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  const availableYears = useMemo(() => {
    const years = Array.from(
      new Set(
        projectList.map((p) =>
          new Date(p.creationDate).getFullYear().toString()
        )
      )
    );
    return years.sort();
  }, [projectList]);

  const monthsByYear = useMemo(() => {
    const map: Record<string, string[]> = {};
    projectList.forEach((p) => {
      const year = new Date(p.creationDate).getFullYear().toString();
      const month = (new Date(p.creationDate).getMonth() + 1)
        .toString()
        .padStart(2, "0");
      if (!map[year]) map[year] = [];
      if (!map[year].includes(month)) map[year].push(month);
    });

    Object.keys(map).forEach((y) => {
      map[y].sort();
    });
    return map;
  }, [projectList]);

  const availableMonths = filters.year ? monthsByYear[filters.year] || [] : [];

  const allTags = useMemo(() => {
    const tagMap = new Map<string, (typeof projectList)[0]["tags"][0]>();

    projectList.forEach((project) => {
      project.tags.forEach((tag) => {
        tagMap.set(tag.name, tag);
      });
    });

    return Array.from(tagMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [projectList]);

  useEffect(() => {
    setAvailableTags(allTags);
  }, [allTags]);

  const handleFilterChange = (field: keyof FilterValues, value: any) => {
    const newFilters = { ...filters, [field]: value };
    if (field === "year" && value === "") {
      newFilters.month = "";
    }

    onChange(newFilters);
  };

  return (
    <Box p={2}>
      <Stack spacing={2} direction="row" flexWrap="wrap">
        <TextField
          label="Search"
          variant="outlined"
          value={filters.searchText}
          onChange={(e) => handleFilterChange("searchText", e.target.value)}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status}
            onChange={(e: SelectChangeEvent) =>
              handleFilterChange("status", e.target.value)
            }
          >
            {[
              "No Filter",
              "Finished",
              "InProgress",
              "Created",
              "QuoteSent",
              "Rejected",
            ].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel shrink>test</InputLabel>
          <Select
            multiple
            value={filters.tags}
            onChange={(event) => {
              const value = event.target.value as number[];
              console.log(value);
              handleFilterChange("tags", value);
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((id) => {
                  const tag = allTags.find((t) => t.id === id);
                  return <Chip key={id} label={tag?.name || id} />;
                })}
              </Box>
            )}
          >
            {availableTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                <Checkbox checked={filters.tags.includes(tag.id)} />
                <ListItemText primary={tag.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="year-label">Year</InputLabel>
          <Select
            labelId="year-label"
            value={filters.year}
            onChange={(e: SelectChangeEvent) =>
              handleFilterChange("year", e.target.value)
            }
            label="Year"
          >
            <MenuItem value="">All Years</MenuItem>
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filters.year && (
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              value={filters.month}
              onChange={(e: SelectChangeEvent) =>
                handleFilterChange("month", e.target.value)
              }
              label="Month"
            >
              <MenuItem value="">All Months</MenuItem>
              {availableMonths.map((monthNum) => {
                const index = parseInt(monthNum, 10) - 1;
                return (
                  <MenuItem key={monthNum} value={monthNum}>
                    {months[index]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
      </Stack>
    </Box>
  );
};
